/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { ApolloError } from "@apollo/client";
import { useParams } from "react-router-dom";
import {
  useAddEnvironmentsToOrgUnitMutation,
  useRemoveEnvFromOrgUnitV2Mutation,
} from "~/generated/graphql";
import { useMutationHandler } from "~/hooks";
import { Maybe } from "~/tools";
import {
  Environment,
  filterApps,
  showUnattachmentToasts,
  splitEnvsByConflict,
} from "../utils";

type RouteParams = {
  structureId: string;
  unitId: string;
};

type Application = {
  name: string;
  environments?: Maybe<Maybe<Environment>[]>;
};

type Props = {
  selectedAllEnvironments: boolean;
  applications: Application[];
  filter: string;
  envs: Maybe<Environment>[];
  envsToRemove: Maybe<Environment>[];
};

type ReturnValues = {
  attachSelectedEnvs: () => Promise<void>;
  unattachSelectedEnvs: () => Promise<void>;
  attachmentConflicts: (Maybe<Environment> | undefined)[];
  setAttachmentConflicts: (
    conflicts: (Maybe<Environment> | undefined)[]
  ) => void;
};

export function useAttachEnvs({
  selectedAllEnvironments,
  applications,
  filter,
  envs,
  envsToRemove,
}: Props): ReturnValues {
  const [attachmentConflicts, setAttachmentConflicts] = React.useState<
    (Maybe<Environment> | undefined)[]
  >([]);
  const { structureId = "", unitId: orgUnitId = "" } = useParams<RouteParams>();
  const [addEnvironmentsToOrgUnit] = useAddEnvironmentsToOrgUnitMutation();
  const [removeEnvFromOrgUnit] = useRemoveEnvFromOrgUnitV2Mutation();
  const { runMutation } = useMutationHandler();

  const attachSelectedEnvs = async () => {
    const envList = detectAttachmentConflicts(
      selectedAllEnvironments
        ? filterApps(applications, filter)
            ?.map((application) => application?.environments)
            .flat()
        : envs
    );

    const envIds = envList.flatMap((env) => (env?.nid ? [env.nid] : []));

    await runMutation({
      mutation: () =>
        addEnvironmentsToOrgUnit({
          variables: {
            input: {
              orgUnitId,
              envIds,
            },
          },
        }),
      successText: `${envIds.length} environments have been attached.`,
    });
  };

  const unattachSelectedEnvs = async () => {
    const errors: [ApolloError, Maybe<Environment>][] = [];
    const requests = envsToRemove.map((env) =>
      removeEnvFromOrgUnit({
        variables: { envId: env?.nid ?? "", orgUnitId },
      }).catch((error: ApolloError) => errors.push([error, env]))
    );

    await Promise.all(requests);

    showUnattachmentToasts(errors, envsToRemove.length);
  };

  const detectAttachmentConflicts = (
    envList: (Maybe<Environment> | undefined)[]
  ) => {
    const [conflicts, readyToSave] = splitEnvsByConflict(envList, structureId);
    setAttachmentConflicts(conflicts);
    return readyToSave;
  };

  return {
    attachSelectedEnvs,
    unattachSelectedEnvs,
    attachmentConflicts,
    setAttachmentConflicts,
  };
}
